import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { useAuth } from "@/api/hooks/useAuth";
import { validateResponse } from "@/api/validateResponse";
import {
  AskOutSchema,
  AsksPageSchema,
  AttachmentOutSchema,
  MessageOutSchema,
  MessagesPageSchema,
  SummaryOutSchema,
  type AskOut,
  type AsksPage,
  type AttachmentOut,
  type MessagesPage,
  type MessageOut,
  type StudioAskStatus,
  type SummaryOut,
} from "@/api/types/studio";

/** Query-key factory — mirrors the outreach hook convention. */
export const studioKeys = {
  all: ["studio"] as const,
  messages: () => [...studioKeys.all, "messages"] as const,
  asks: (status?: StudioAskStatus) => [...studioKeys.all, "asks", status ?? "all"] as const,
  summary: () => [...studioKeys.all, "summary"] as const,
};

function invalidateStudio(queryClient: QueryClient, ...scopes: ("messages" | "asks" | "summary")[]) {
  // `asks` uses a prefix key so every status-filtered list invalidates.
  const keys = {
    messages: studioKeys.messages(),
    asks: ["studio", "asks"] as const,
    summary: studioKeys.summary(),
  };
  for (const scope of scopes) {
    void queryClient.invalidateQueries({ queryKey: keys[scope] });
  }
}

/** Poll only while the user is actually looking at the tab. */
function visibleInterval(ms: number) {
  return () => (typeof document !== "undefined" && document.visibilityState === "visible" ? ms : false);
}

function isHidden() {
  return typeof document === "undefined" || document.visibilityState !== "visible";
}

/** 4 s while the assistant owes a reply, else the normal cadence. */
const FAST_POLL_MS = 4_000;

/**
 * The one thread, paged backwards. Page 1 is the newest 50 messages;
 * fetchNextPage pulls the next-older page via `next_before`.
 */
export function useStudioMessages(limit = 50) {
  const queryClient = useQueryClient();
  return useInfiniteQuery({
    queryKey: [...studioKeys.messages(), limit] as const,
    queryFn: async ({ pageParam }: { pageParam: string }) => {
      const searchParams = new URLSearchParams();
      searchParams.set("limit", String(limit));
      if (pageParam) searchParams.set("before", pageParam);

      const { data } = await apiClient.get(`/v2/studio/messages?${searchParams.toString()}`);
      return validateResponse(MessagesPageSchema, data, "GET /v2/studio/messages");
    },
    initialPageParam: "" as string,
    getNextPageParam: (lastPage: MessagesPage) => lastPage.next_before ?? undefined,
    refetchInterval: (query) => {
      if (isHidden()) return false;
      // Pages are newest-first, so pages[0]'s last item is the thread's
      // newest message — same test as waitingForAssistant() in the studio
      // feature (kept inline here to avoid hooks→features imports).
      const latest = query.state.data?.pages[0]?.items ?? [];
      const summary = queryClient.getQueryData<SummaryOut>(studioKeys.summary());
      const waiting =
        (summary?.unprocessed_from_people ?? 0) > 0 &&
        latest.length > 0 &&
        latest[latest.length - 1].author !== "assistant";
      return waiting ? FAST_POLL_MS : 15_000;
    },
  });
}

export interface SendMessageInput {
  body: string;
  kind: "chat" | "feedback";
  page?: string | null;
  attachment_ids?: string[];
  /** Only for the optimistic bubble; never posted. */
  attachments?: AttachmentOut[];
}

export function useSendMessage() {
  const queryClient = useQueryClient();
  const { role } = useAuth();
  return useMutation<
    MessageOut,
    unknown,
    SendMessageInput,
    { previous: [QueryKey, InfiniteData<MessagesPage> | undefined][] }
  >({
    mutationFn: async ({ body, kind = "chat", page = null, attachment_ids = [] }: SendMessageInput) => {
      const { data } = await apiClient.post("/v2/studio/messages", {
        body,
        kind,
        page,
        attachment_ids,
      });
      return validateResponse(MessageOutSchema, data, "POST /v2/studio/messages");
    },
    // Optimistic bubble: the assistant can take minutes to answer, so show
    // the sender's message immediately instead of waiting for the POST.
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: studioKeys.messages() });
      const previous = queryClient.getQueriesData<InfiniteData<MessagesPage>>({
        queryKey: studioKeys.messages(),
      });
      const temp: MessageOut = {
        id: `temp-${Date.now()}`,
        author: role === "admin" ? "admin" : "owner",
        author_user_id: null,
        kind: vars.kind,
        body: vars.body,
        page: vars.page ?? null,
        attachments: vars.attachments ?? [],
        ask_id: null,
        processed_at: null,
        created_at: new Date().toISOString(),
      };
      queryClient.setQueriesData<InfiniteData<MessagesPage>>(
        { queryKey: studioKeys.messages() },
        (data) => {
          // First page = newest 50; append there so the bubble lands at the
          // bottom. No cached pages yet: seed one (invalidated on settle).
          if (!data || data.pages.length === 0) {
            return { pages: [{ items: [temp], next_before: null }], pageParams: [""] };
          }
          return {
            ...data,
            pages: data.pages.map((page, i) =>
              i === 0 ? { ...page, items: [...page.items, temp] } : page,
            ),
          };
        },
      );
      return { previous };
    },
    onError: (_error, _vars, context) => {
      for (const [key, value] of context?.previous ?? []) {
        queryClient.setQueryData(key, value);
      }
    },
    onSuccess: () => invalidateStudio(queryClient, "messages", "summary"),
  });
}

/**
 * Upload one file to POST /studio/attachments and resolve with its
 * AttachmentOut. The message POST then claims it via attachment_ids.
 *
 * This is a plain awaited function, not a shared useMutation: the composer
 * runs several uploads at once, and per-call mutate callbacks on one
 * mutation only ever settle for the most recent call.
 *
 * Content-Type must be explicitly unset (not set to the literal string
 * "multipart/form-data"): the client's instance-level application/json
 * default would otherwise make axios JSON-serialize the FormData, but a
 * hardcoded "multipart/form-data" string is just as broken — it ships
 * without the required `boundary=...` parameter, since axios only lets the
 * browser compute and attach the boundary when no Content-Type header is
 * set at all. Both variants fail the upload; `undefined` deletes the
 * inherited default so the browser sets the full multipart header itself.
 */
export async function uploadStudioAttachment(
  file: File,
  onProgress?: (percent: number) => void,
): Promise<AttachmentOut> {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await apiClient.post("/v2/studio/attachments", formData, {
    headers: { "Content-Type": undefined },
    onUploadProgress: (e) => {
      if (!onProgress || !e.total) return;
      onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  return validateResponse(AttachmentOutSchema, data, "POST /v2/studio/attachments");
}

export function useStudioAsks(status?: StudioAskStatus) {
  return useQuery<AsksPage>({
    queryKey: studioKeys.asks(status),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (status) searchParams.set("status", status);

      const { data } = await apiClient.get(`/v2/studio/asks?${searchParams.toString()}`);
      return validateResponse(AsksPageSchema, data, "GET /v2/studio/asks");
    },
    refetchInterval: visibleInterval(30_000),
  });
}

export interface DecideAskInput {
  id: string;
  status: StudioAskStatus;
  decision?: string | null;
}

/**
 * Admin-only decision on a held ask. Approve = `in_progress`, decline =
 * `declined` (both insert the approval thread row server-side), so the
 * thread + summary need invalidating alongside the asks list.
 */
export function useDecideAsk() {
  const queryClient = useQueryClient();
  return useMutation<AskOut, unknown, DecideAskInput>({
    mutationFn: async ({ id, status, decision = null }) => {
      const { data } = await apiClient.patch(`/v2/studio/asks/${id}`, { status, decision });
      return validateResponse(AskOutSchema, data, "PATCH /v2/studio/asks/:id");
    },
    onSuccess: () => invalidateStudio(queryClient, "asks", "messages", "summary"),
  });
}

export function useStudioSummary() {
  return useQuery<SummaryOut>({
    queryKey: studioKeys.summary(),
    queryFn: async () => {
      const { data } = await apiClient.get("/v2/studio/summary");
      return validateResponse(SummaryOutSchema, data, "GET /v2/studio/summary");
    },
    refetchInterval: (query) => {
      if (isHidden()) return false;
      return (query.state.data?.unprocessed_from_people ?? 0) > 0 ? FAST_POLL_MS : 30_000;
    },
  });
}
